import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { BLOCK_TYPE_LABEL } from './pageSchemaModel';
import type { Block } from './blocks';
import type { Catalog } from './useCatalog';
import { formatRate } from '../stores/formatRate';
import {
  boostBadgeText,
  type StoreRateProjection,
} from './storeRateProjection';

/**
 * One block rendered as a STRUCTURAL-HONEST representation (D-page-schema-editor-8c).
 * NOT a pixel render of the apps: there are NO shared app rendering components in
 * this repo (the apps' block renderer lives cross-repo), so a pixel mirror would be a
 * duplicate build that drifts silently with no fidelity check. Instead each block is
 * a faithful representation of its type plus its configured data plus its reference-
 * resolved content (the honesty-over-richness posture of the rate-rules conflict
 * preview and the tracking diagnostics), labelled a representation not a render.
 *
 * RATE PROJECTION on store cards (D-page-schema-editor-8c-boost): a store-bearing card
 * shows the resolved BRAND NAME plus the PROJECTED effective rate for the selected
 * tier (the #64 resolver read at the region grain), labelled with the tier, with a
 * boost badge when a rule wins. It is a projection over today's rules, NOT a member's
 * realised rate. A slug that does not resolve to a region (a dangling reference) keeps
 * the original honest-by-omission behaviour: the raw slug, NO rate (the resolver is
 * never called for it). References resolve via useCatalog (slug -> name); an unresolved
 * slug shows the RAW slug, it does not fabricate a name.
 */

/**
 * A previewed store: the resolved brand NAME, plus the tier-labelled projected rate
 * (and a boost badge) when the slug resolves to a region. No rate when it does not.
 */
const StoreCard = ({
  slug,
  resolveStore,
  projection,
}: {
  slug: string;
  resolveStore: Catalog['resolveStore'];
  projection?: StoreRateProjection;
}) => {
  const res = projection && projection.ready ? projection.resolve(slug) : null;
  return (
    <Box
      data-testid={`preview-store-${slug}`}
      sx={{
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        minWidth: 96,
      }}
    >
      <Typography variant="body2">{resolveStore(slug)}</Typography>
      {res && projection && (
        <Box sx={{ mt: 0.5 }} data-testid="store-rate">
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="store-rate-value"
            >
              {res.winner
                ? `${formatRate(res.baseRate)} to ${formatRate(res.effectiveRate)} (${projection.tier})`
                : `${formatRate(res.effectiveRate)} (${projection.tier})`}
            </Typography>
            {res.winner && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={boostBadgeText(res.winner)}
                data-testid="store-boost-badge"
              />
            )}
          </Stack>
          {res.winner?.visibleUntil && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              ends {res.winner.visibleUntil.slice(0, 10)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

/** A previewed category: the resolved label (or the raw slug on a miss). */
const CategoryChip = ({
  slug,
  resolveCategory,
}: {
  slug: string;
  resolveCategory: Catalog['resolveCategory'];
}) => (
  <Chip
    size="small"
    variant="outlined"
    label={resolveCategory(slug)}
    data-testid={`preview-category-${slug}`}
  />
);

/** A small caption naming the representation choice for a block (variant, link, align). */
const Meta = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ display: 'block' }}
  >
    {children}
  </Typography>
);

export const BlockPreview = ({
  block,
  catalog,
  projection,
}: {
  block: Block;
  catalog: Catalog;
  projection?: StoreRateProjection;
}) => {
  const { resolveStore, resolveCategory } = catalog;

  const body = (() => {
    switch (block.type) {
      case 'hero':
        return (
          <Stack spacing={1}>
            {block.data.image ? (
              <Box
                component="img"
                src={block.data.image}
                alt=""
                data-testid="preview-hero-image"
                sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'cover' }}
              />
            ) : (
              <Box
                data-testid="preview-hero-image"
                sx={{
                  height: 80,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Meta>Image</Meta>
              </Box>
            )}
            <Typography variant="h6">{block.data.title}</Typography>
            {block.data.subtitle && (
              <Typography variant="body2" color="text.secondary">
                {block.data.subtitle}
              </Typography>
            )}
            {block.data.cta && (
              <Chip
                label={block.data.cta.label}
                color="primary"
                data-testid="preview-hero-cta"
              />
            )}
          </Stack>
        );

      case 'carousel': {
        const stores = block.data.storeIds ?? [];
        const cats = block.data.categoryIds ?? [];
        return (
          <Stack spacing={1}>
            <Meta>Carousel, {block.data.variant} variant</Meta>
            {block.data.title && (
              <Typography variant="subtitle2">{block.data.title}</Typography>
            )}
            {stores.length > 0 && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {stores.map((slug) => (
                  <StoreCard
                    key={slug}
                    slug={slug}
                    resolveStore={resolveStore}
                    projection={projection}
                  />
                ))}
              </Stack>
            )}
            {cats.length > 0 && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {cats.map((slug) => (
                  <CategoryChip
                    key={slug}
                    slug={slug}
                    resolveCategory={resolveCategory}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        );
      }

      case 'storeGrid':
        return (
          <Stack spacing={1}>
            {block.data.title && (
              <Typography variant="subtitle2">{block.data.title}</Typography>
            )}
            <Box
              data-testid="preview-store-grid"
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${block.data.columns}, 1fr)`,
                gap: 1,
              }}
            >
              {block.data.storeIds.map((slug) => (
                <StoreCard
                  key={slug}
                  slug={slug}
                  resolveStore={resolveStore}
                  projection={projection}
                />
              ))}
            </Box>
          </Stack>
        );

      case 'categoryStrip':
        return (
          <Stack spacing={1}>
            <Meta>{block.data.variant} variant</Meta>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {block.data.categoryIds.map((slug) => (
                <CategoryChip
                  key={slug}
                  slug={slug}
                  resolveCategory={resolveCategory}
                />
              ))}
            </Stack>
          </Stack>
        );

      case 'banner':
        return (
          <Stack spacing={1}>
            {block.data.image ? (
              <Box
                component="img"
                src={block.data.image}
                alt=""
                data-testid="preview-banner-image"
                sx={{ maxWidth: '100%', maxHeight: 120, objectFit: 'cover' }}
              />
            ) : (
              <Box
                data-testid="preview-banner-image"
                sx={{ height: 60, bgcolor: 'action.hover' }}
              />
            )}
            <Meta>{block.data.variant} variant</Meta>
            <Typography variant="body2" data-testid="preview-banner-link">
              Links to {block.data.link.kind}: {block.data.link.ref}
            </Typography>
          </Stack>
        );

      case 'featuredStore':
        return (
          <Stack spacing={1}>
            {block.data.headline && (
              <Typography variant="subtitle2">{block.data.headline}</Typography>
            )}
            <StoreCard
              slug={block.data.storeId}
              resolveStore={resolveStore}
              projection={projection}
            />
          </Stack>
        );

      case 'textBlock':
        return (
          <Stack spacing={1}>
            {block.data.title && (
              <Typography variant="subtitle2">{block.data.title}</Typography>
            )}
            <Typography
              variant="body2"
              data-testid="preview-text-body"
              sx={{ textAlign: block.data.align ?? 'left' }}
            >
              {block.data.body}
            </Typography>
          </Stack>
        );

      case 'divider': {
        // The style shown as a representative rule or space.
        if (block.data.style === 'space') {
          return (
            <Box>
              <Meta>space</Meta>
              <Box data-testid="preview-divider" sx={{ height: 24 }} />
            </Box>
          );
        }
        return (
          <Box>
            <Meta>{block.data.style}</Meta>
            <Divider
              data-testid="preview-divider"
              sx={{
                borderBottomWidth: block.data.style === 'thick' ? 4 : 1,
              }}
            />
          </Box>
        );
      }

      case 'pillCta': {
        const tone = block.data.tone ?? 'primary';
        const color =
          tone === 'primary'
            ? 'primary'
            : tone === 'secondary'
              ? 'secondary'
              : 'default';
        return (
          <Stack spacing={1} alignItems="flex-start">
            <Chip
              label={block.data.label}
              color={color}
              data-testid="preview-pill"
              data-tone={tone}
            />
            <Typography variant="body2" data-testid="preview-pill-link">
              Links to {block.data.link.kind}: {block.data.link.ref}
            </Typography>
          </Stack>
        );
      }
    }
  })();

  return (
    <Box
      data-testid={`preview-block-${block.id}`}
      data-block-type={block.type}
      sx={{
        p: 2,
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Chip
        size="small"
        label={BLOCK_TYPE_LABEL[block.type]}
        data-testid={`preview-type-${block.id}`}
        sx={{ mb: 1 }}
      />
      {body}
    </Box>
  );
};
